// Wire contract for browser telemetry: POST /api/v1/telemetry/web-perf.
//
// The zod schemas ARE the contract; every event type is derived from its
// schema via z.infer, so shape and validation can never drift. Shared by the
// three parties on the wire:
//   - packages/web enqueues these events through the perf beacon,
//   - the API route validates with these schemas (layering server-only
//     policy such as the client-span clock-skew bound on top), and
//   - common-backend records metrics / relays client spans from them.
// Field names are snake_case because these are serialized payloads, not
// in-process structs. Bounds encoded here are wire contract: they describe
// what a well-formed event IS, not how a given server chooses to treat it.
import { z } from 'zod'

/**
 * Server-side cap on events per web-perf batch (the route rejects larger
 * batches outright), and therefore the size at which the client beacon must
 * flush. A client threshold above this cap would turn every full batch into
 * a 400 — the two values are one contract.
 */
export const WEB_TELEMETRY_MAX_EVENTS_PER_BATCH = 30

/**
 * Upper bound on any reported duration (value_ms, span duration): the API
 * rejects values above it and the metrics layer clamps page loads to it (the
 * duration histogram's top bucket boundary matches).
 */
export const WEB_TELEMETRY_MAX_DURATION_MS = 120_000

/**
 * The web client's page-type universe: the router derives its PageType union
 * from this, and the API telemetry route uses it as the bounded label set for
 * page_load metrics (unknown values collapse to 'other').
 */
export const WEB_PAGE_TYPES = [
  'home',
  'table',
  'tableSettings',
  'createTable',
  'chart',
  'chartSettings',
  'createChart',
  'pivotTable',
  'pivotTableSettings',
  'createPivotTable',
  'form',
  'formSettings',
  'createForm',
  'formsHome',
  'record',
  'workspace',
  'createWorkspace',
  'workflow',
  'dossbot',
  'settings',
  'apollo',
  'storybook',
  'dependencyExplorer',
] as const

export type WebPageType = (typeof WEB_PAGE_TYPES)[number]

/**
 * Closed universe of browser span names. Enforced by the client_span schema,
 * so clients can never mint arbitrary span names into Cloud Trace.
 */
export const WEB_CLIENT_SPAN_NAMES = [
  'guest_frame.load',
  'guest_frame.boot',
  'guest_frame.first_render',
  'guest_frame.initial_queries',
  'graphql.client_fetch',
] as const

export type WebClientSpanName = (typeof WEB_CLIENT_SPAN_NAMES)[number]

/** Browser epoch milliseconds at enqueue time; informational. */
const eventTimestampSchema = z.number().optional()
const boundedDurationSchema = z.number().nonnegative().max(WEB_TELEMETRY_MAX_DURATION_MS)
const boundedCountSchema = z.number().int().nonnegative().max(10_000_000)
const boundedBytesSchema = z
  .number()
  .int()
  .nonnegative()
  .max(8 * 1024 * 1024 * 1024)

// 'abandoned' page loads carry the elapsed time when the user left mid-load
// (navigation away, reload, tab close) — a lower bound on a load duration that
// was never observed to finish. value_ms is deliberately unbounded here: the
// metrics layer clamps and counts overflow rather than dropping the sample.
export const webPageLoadEventSchema = z.strictObject({
  type: z.literal('page_load'),
  page_type: z.string().min(1).max(64),
  load_type: z.enum(['initial', 'route_change']),
  status: z.enum(['complete', 'interrupted', 'timeout', 'abandoned']),
  value_ms: z.number(),
  ts: eventTimestampSchema,
})

export type WebPageLoadEvent = z.infer<typeof webPageLoadEventSchema>
export type WebPageLoadType = WebPageLoadEvent['load_type']
export type WebPageLoadStatus = WebPageLoadEvent['status']

export const webGuestFrameLifecycleEventSchema = z.strictObject({
  type: z.literal('guest_frame_lifecycle'),
  operation: z.enum(['compile', 'boot', 'first_render', 'initial_queries', 'runtime']),
  status: z.enum(['succeeded', 'failed', 'timeout']),
  error_code: z
    .enum([
      'compile_error',
      'boot_timeout',
      'protocol_mismatch',
      'module_load_error',
      'render_error',
      'unhandled_error',
      'unhandled_rejection',
    ])
    .optional(),
  value_ms: boundedDurationSchema,
  frame_count: boundedCountSchema.max(256),
  /** Which execution path hosted the embed; absent on events from before the ESM path shipped. */
  path: z.enum(['esm', 'legacy']).optional(),
  ts: eventTimestampSchema,
})

export type WebGuestFrameLifecycleEvent = z.infer<typeof webGuestFrameLifecycleEventSchema>
export type WebGuestFramePath = NonNullable<WebGuestFrameLifecycleEvent['path']>

export const webGuestFrameResourceEventSchema = z.strictObject({
  type: z.literal('guest_frame_resource'),
  status: z.enum(['succeeded', 'partial', 'failed']),
  frame_count: boundedCountSchema.max(256),
  visible_frame_count: boundedCountSchema.max(256),
  responding_frame_count: boundedCountSchema.max(256),
  dom_nodes: boundedCountSchema,
  long_task_count: boundedCountSchema,
  long_task_duration_ms: boundedDurationSchema,
  max_long_task_duration_ms: boundedDurationSchema,
  sample_window_ms: z
    .number()
    .nonnegative()
    .max(30 * 60_000),
  memory_source: z.enum(['user_agent_specific', 'chromium_heap_estimate', 'unsupported']),
  memory_used_bytes: boundedBytesSchema.optional(),
  memory_total_bytes: boundedBytesSchema.optional(),
  transfer_bytes: boundedBytesSchema,
  decoded_bytes: boundedBytesSchema,
  cache_hit_count: boundedCountSchema,
  ts: eventTimestampSchema,
})

export type WebGuestFrameResourceEvent = z.infer<typeof webGuestFrameResourceEventSchema>

export const webGuestQueryEventSchema = z.strictObject({
  type: z.literal('guest_query'),
  status: z.enum(['succeeded', 'failed', 'truncated']),
  source: z.enum(['client', 'cache', 'backend']),
  error_code: z
    .enum([
      'query_validation',
      'query_budget_exceeded',
      'query_timeout',
      'query_failed',
      'result_too_large',
      'row_limit_too_large',
    ])
    .optional(),
  // Anonymous frame-local identity groups page fetches without emitting SQL text.
  statement_id: z
    .string()
    .regex(/^[0-9a-f]{32}$/)
    .optional(),
  statement_request_index: boundedCountSchema.optional(),
  // Backend trace id of the fetch, joining this event to Cloud Trace.
  trace_id: z
    .string()
    .regex(/^[0-9a-f]{32}$/)
    .optional(),
  value_ms: boundedDurationSchema,
  ts: eventTimestampSchema,
})

export type WebGuestQueryEvent = z.infer<typeof webGuestQueryEventSchema>

export const webTraceIdSchema = z
  .string()
  .regex(/^[0-9a-f]{32}$/)
  .refine((value) => value !== '0'.repeat(32), 'all-zero trace id is invalid')
export const webSpanIdSchema = z
  .string()
  .regex(/^[0-9a-f]{16}$/)
  .refine((value) => value !== '0'.repeat(16), 'all-zero span id is invalid')

/**
 * A browser-minted span, relayed to the OTel collector by the API with its
 * client-minted W3C ids intact (client-span-relay.ts). Everything is bounded:
 * names come from a closed enum, ids are well-formed non-zero W3C values, and
 * a span never exceeds the duration ceiling. The API layers its clock-skew
 * policy on top of this schema — that bound is server policy, not contract.
 */
export const webClientSpanEventSchema = z
  .strictObject({
    type: z.literal('client_span'),
    trace_id: webTraceIdSchema,
    span_id: webSpanIdSchema,
    parent_span_id: webSpanIdSchema.optional(),
    name: z.enum(WEB_CLIENT_SPAN_NAMES),
    /** Browser epoch milliseconds. */
    start_ms: z.number().nonnegative(),
    end_ms: z.number().nonnegative(),
    status: z.enum(['ok', 'error', 'timeout']),
    error_code: z
      .string()
      .regex(/^[a-z0-9_]{1,64}$/)
      .optional(),
    embed_id: z.uuid().optional(),
    view_content_id: z.uuid().optional(),
    ts: eventTimestampSchema,
  })
  .refine((event) => event.end_ms >= event.start_ms, 'end_ms must not precede start_ms')
  .refine(
    (event) => event.end_ms - event.start_ms <= WEB_TELEMETRY_MAX_DURATION_MS,
    'span duration exceeds the maximum',
  )

export type WebClientSpanEvent = z.infer<typeof webClientSpanEventSchema>

export type WebTelemetryEvent =
  | WebPageLoadEvent
  | WebGuestFrameLifecycleEvent
  | WebGuestFrameResourceEvent
  | WebGuestQueryEvent
  | WebClientSpanEvent
